import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export type TutorialStep = {
  id: string;
  title: string;
  content: string;
  targetSelector?: string;
  position: 'top' | 'right' | 'bottom' | 'left' | 'center';
  action?: string;
  requiredAction?: boolean;
  nextPath?: string;
  previousPath?: string;
};

type TutorialState = {
  active: boolean;
  currentStepIndex: number;
  completed: boolean;
  steps: TutorialStep[];
  skipTutorial: () => void;
  startTutorial: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  completeTutorial: () => void;
  currentStep: TutorialStep | null;
  isNavigating: boolean;
  isTransitioning: boolean;
};

const TutorialContext = createContext<TutorialState | undefined>(undefined);

const tutorialSteps: TutorialStep[] = [
  {
    id: 'dashboard',
    title: "Welcome to IELTS With AI",
    content: "Let's take a quick tour around. This is your personal dashboard where you can track your progress and access practice sessions.",
    targetSelector: ".dashboard-container",
    position: "center",
  },
  {
    id: 'task-selection',
    title: "Choose Your Task Type",
    content: "IELTS has two writing tasks. Task 1 involves describing visual information, while Task 2 requires writing an essay on a given topic.",
    targetSelector: ".task-selection",
    position: "bottom",
    nextPath: "/tutorial",
  },
  {
    id: 'topic-selection',
    title: "Choose a Topic",
    content: "Select a topic that interests you. We have more than 400 topics top pick from for Task 2 and about 300 for Task 1!",
    targetSelector: ".topic-selector",
    position: "top",
    previousPath: "/dashboard"
  },
  {
    id: 'writing-interface',
    title: "Writing Interface",
    content: "This is where you'll write your essay. Notice the word counter to help you meet the minimum word requirement.",
    targetSelector: ".writing-area",
    position: "top",
  },
  {
    id: 'submit-essay',
    title: "Submit Your Essay",
    content: "When you're ready, click the 'Submit' button to receive AI feedback on your writing. You can always try again to improve your score!",
    targetSelector: ".submit-button",
    position: "top",
  },
  {
    id: 'feedback-overview',
    title: "Feedback Overview",
    content: "After submission, you'll receive detailed feedback on your writing, including strengths, weaknesses, and a score prediction.",
    targetSelector: ".feedback-container",
    position: "top",
    nextPath: "/dashboard"
  },
  {
    id: 'progress-tracking',
    title: "You're All Set!",
    content: "The chart in the dashboard shows your score improvement as you practice more. Continue practicing to improve your IELTS writing score!",
    targetSelector: ".progress-charts",
    position: "top",
    previousPath: "/tutorial"
  }
];

export const TutorialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [steps] = useState<TutorialStep[]>(tutorialSteps);

  // Load tutorial state from database or localStorage
  useEffect(() => {
    const loadTutorialState = async () => {
      try {
        if (user) {
          // Check if user already has a preference entry
          const { data: existingData, error: checkError } = await supabase
            .from('user_preferences')
            .select('id, tutorial_completed, tutorial_step')
            .eq('user_id', user.id);

          if (checkError) {
            loadFromLocalStorage();
            return;
          }

          // If no existing preferences, create a new entry
          if (!existingData || existingData.length === 0) {
            const { error: insertError } = await supabase.from('user_preferences').insert({
              user_id: user.id,
              tutorial_completed: false,
              tutorial_step: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

            if (insertError) {
              loadFromLocalStorage();
              return;
            }

            setCompleted(false);
            setCurrentStepIndex(0);
            return;
          }

          // We have existing data, use the first entry
          // If multiple entries exist (legacy data), we'll use the first one
          const preferenceData = existingData[0];
          setCompleted(preferenceData.tutorial_completed || false);
          setCurrentStepIndex(preferenceData.tutorial_step || 0);
        } else {
          // Use localStorage for non-authenticated users
          loadFromLocalStorage();
        }
      } catch (error) {
        loadFromLocalStorage();
      }
    };

    const loadFromLocalStorage = () => {
      const savedState = localStorage.getItem('tutorial_state');
      if (savedState) {
        try {
          const { completed, step } = JSON.parse(savedState);
          setCompleted(completed || false);
          setCurrentStepIndex(step || 0);
        } catch (e) {
          console.error('Failed to parse localStorage tutorial state');
          setCompleted(false);
          setCurrentStepIndex(0);
        }
      }
    };

    loadTutorialState();
  }, [user]);

  // Save tutorial state to database or localStorage
  const saveTutorialState = useCallback(async (completed: boolean, step: number) => {
    try {
      if (user) {
        // First check if user already has a preference entry
        const { data, error: checkError } = await supabase
          .from('user_preferences')
          .select('id')
          .eq('user_id', user.id);

        if (checkError) {
          localStorage.setItem('tutorial_state', JSON.stringify({ completed, step }));
          return;
        }

        // If no existing preferences, create a new entry
        if (!data || data.length === 0) {
          const { error: insertError } = await supabase.from('user_preferences').insert({
            user_id: user.id,
            tutorial_completed: completed,
            tutorial_step: step,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

          if (insertError) {
            localStorage.setItem('tutorial_state', JSON.stringify({ completed, step }));
          }
        } else {
          // Update the existing entry
          const { error: updateError } = await supabase
            .from('user_preferences')
            .update({
              tutorial_completed: completed,
              tutorial_step: step,
              updated_at: new Date().toISOString()
            })
            .eq('id', data[0].id);

          if (updateError) {
            localStorage.setItem('tutorial_state', JSON.stringify({ completed, step }));
          }
        }
      } else {
        // Use localStorage for non-authenticated users
        localStorage.setItem('tutorial_state', JSON.stringify({ completed, step }));
      }
    } catch (error) {
      console.error('Failed to save tutorial state:', error);
      // Fall back to localStorage if there's an error
      localStorage.setItem('tutorial_state', JSON.stringify({ completed, step }));
    }
  }, [user]);

  // Tutorial control functions
  const skipTutorial = () => {
    setActive(false);
    setCompleted(true);
    saveTutorialState(true, currentStepIndex);
    toast.success('Tutorial skipped. You can restart it anytime from the dashboard.');
    navigate('/dashboard');
  };

  const startTutorial = () => {
    setActive(true);
    setCurrentStepIndex(0);
    setCompleted(false);
    saveTutorialState(false, 0);
  };

  // This function is defined below

  const nextStep = () => {
    if (currentStepIndex < tutorialSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      const nextStepObj = tutorialSteps[nextIndex];
      const currentStepObj = tutorialSteps[currentStepIndex];

      // Check if the current step has a nextPath for navigation
      if (currentStepObj.nextPath && !isNavigating) {
        setIsNavigating(true);
        setIsTransitioning(true); // Hide highlight and tooltip during navigation

        // Short delay to allow state to be saved before navigation
        setTimeout(() => {
          // Save state before navigation
          setCurrentStepIndex(nextIndex);
          saveTutorialState(false, nextIndex);

          // Navigate to the new page
          navigate(currentStepObj.nextPath!);

          // Add a delay after navigation to ensure DOM elements are loaded
          setTimeout(() => {
            setIsNavigating(false);
            // Wait a bit longer before showing the highlight to ensure page is fully rendered
            setTimeout(() => {
              setIsTransitioning(false); // Show highlight and tooltip after page is loaded
            }, 300);
          }, 500);
        }, 100);
      } else {
        // If no navigation path, just update the state without navigating
        setCurrentStepIndex(nextIndex);
        saveTutorialState(false, nextIndex);
        setIsNavigating(false);
      }
    } else {
      // Complete the tutorial
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      const newIndex = currentStepIndex - 1;
      const prevStepObj = tutorialSteps[newIndex];
      const currentStepObj = tutorialSteps[currentStepIndex];

      // Check if current step has a previousPath for navigation
      if (currentStepObj.previousPath && !isNavigating) {
        setIsNavigating(true);
        setIsTransitioning(true); // Hide highlight and tooltip during navigation

        // Short delay to allow state to be saved before navigation
        setTimeout(() => {
          // Save state before navigation
          setCurrentStepIndex(newIndex);
          saveTutorialState(false, newIndex);

          // Navigate to the previousPath of the current step
          navigate(currentStepObj.previousPath!);

          // Add a delay after navigation to ensure DOM elements are loaded
          setTimeout(() => {
            setIsNavigating(false);
            // Wait a bit longer before showing the highlight to ensure page is fully rendered
            setTimeout(() => {
              setIsTransitioning(false); // Show highlight and tooltip after page is loaded
            }, 300);
          }, 500);
        }, 100);
      } else {
        // If no navigation path, just update the state without navigating
        setCurrentStepIndex(newIndex);
        saveTutorialState(false, newIndex);
        setIsNavigating(false);
      }
    }
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < tutorialSteps.length) {
      const targetStep = tutorialSteps[step];
      const currentStepObj = tutorialSteps[currentStepIndex];
      const movingForward = step > currentStepIndex;

      // Save state first
      setCurrentStepIndex(step);
      saveTutorialState(false, step);

      // Handle navigation if needed
      // If moving forward, use the nextPath of the current step
      // If moving backward, use the previousPath of the current step
      const navigationPath = movingForward ?
        (currentStepObj.nextPath) :
        (currentStepObj.previousPath);

      if (navigationPath && !isNavigating) {
        setIsNavigating(true);
        // Short delay to allow state to be saved before navigation
        setTimeout(() => {
          navigate(navigationPath);

          // Add a delay after navigation to ensure DOM elements are loaded
          setTimeout(() => {
            setIsNavigating(false);
          }, 500);
        }, 100);
      } else {
        // If no navigation path, just update the state without navigating
        setIsNavigating(false);
      }
    }
  };

  // This function is called when the tutorial is completed
  const completeTutorial = () => {
    setActive(false);
    setCompleted(true);
    saveTutorialState(true, tutorialSteps.length - 1);
    toast.success('Congratulations! You\'ve completed the tutorial.');
    navigate('/dashboard');
  };

  const currentStep = steps[currentStepIndex] || null;

  return (
    <TutorialContext.Provider
      value={{
        active,
        currentStepIndex,
        currentStep,
        steps,
        completed,
        startTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        completeTutorial,
        goToStep,
        isNavigating,
        isTransitioning
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
